import React, { useState } from 'react';
import { FaUpload, FaTrash, FaCheckCircle, FaCamera, FaRobot, FaMagic } from 'react-icons/fa';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { FileUpload } from './File-Upload';
import { extractMedicationsFromFile } from '../services/api';
import {
  FREQUENCY_OPTIONS,
  INDICATION_OPTIONS,
} from '../utils/medicationOptions';
import {
  DURATION_OPTIONS, 
} from '../utils/constants';


const MedicationExtractor = ({ onMedicationsExtracted }) => {
  const [extractedMeds, setExtractedMeds] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadType, setUploadType] = useState('prescription');

  const handleFileUpload = async (files) => {
    const file = files[0];
    if (!file) return;

    setIsLoading(true);

    try {
      const data = await extractMedicationsFromFile({ file, uploadType });
      setExtractedMeds((data.medications || []).map((med, idx) => ({
        ...med,
        id: idx,
        isEditing: false
      })));
    } catch (error) {
      const errorDetail = error?.response?.data?.detail;
      const errorMessage =
        typeof errorDetail === 'string'
          ? errorDetail
          : (errorDetail ? JSON.stringify(errorDetail) : error.message);
      alert(
        'Error extracting medications: ' +
          errorMessage +
          '\n\nIf this is the first request in a while, start the backend and retry.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (id, field, value) => {
    setExtractedMeds(meds =>
      meds.map(m => m.id === id ? { ...m, [field]: value } : m)
    );
  };

  const handleDelete = (id) => {
    setExtractedMeds(meds => meds.filter(m => m.id !== id));
  };

  const handleConfirm = () => {
    const formatted = extractedMeds.map(m => ({
      generic_name: m.generic_name,
      brand_name: m.brand_name || '',
      dose: m.dose,
      frequency: m.frequency,
      indication: m.indication || '',
      duration: m.duration || 'unknown'
    }));
    onMedicationsExtracted(formatted);
  };

  return (
    <div className="neo-card p-4 sm:p-6 mb-8 bg-neo-card">
      <div className="flex items-center gap-4 mb-6">
        <div className="w-14 h-14 bg-neo-accent rounded-lg border-2 border-neo-main shadow-neo-sm flex items-center justify-center">
          <FaRobot className="text-2xl text-white" />
        </div>
        <div>
          <h3 className="text-xl font-black text-neo-text flex items-center gap-2">
            AI Medication Extraction
            
          </h3>
          <p className="text-neo-text-sub text-sm font-medium">Upload prescription or photo to auto-extract medications</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <button
          type="button"
          onClick={() => setUploadType('prescription')}
          className={`p-3 sm:p-4 rounded-lg border-2 transition-all duration-100 flex items-center gap-3 ${
            uploadType === 'prescription'
              ? 'bg-neo-accent text-white border-neo-main shadow-neo-brutal translate-x-[-2px] translate-y-[-2px]'
              : 'bg-neo-card text-neo-text border-neo-main hover:bg-white shadow-neo-sm'
          }`}
        >
          <div className={`w-10 h-10 rounded-md border-2 border-neo-main flex items-center justify-center ${
            uploadType === 'prescription' ? 'bg-white/20' : 'bg-neo-bg'
          }`}>
            <FaUpload className={uploadType === 'prescription' ? 'text-white' : 'text-neo-text'} />
          </div>
          <div className="text-left">
            <p className="font-bold">Prescription Upload</p>
            <p className={`text-xs ${uploadType === 'prescription' ? 'text-white/90' : 'text-neo-text-sub'}`}>
              PDF or image file
            </p>
          </div>
        </button>

        <button
          type="button"
          onClick={() => setUploadType('brown-bag')}
          className={`p-3 sm:p-4 rounded-lg border-2 transition-all duration-100 flex items-center gap-3 ${
            uploadType === 'brown-bag'
              ? 'bg-neo-accent text-white border-neo-main shadow-neo-brutal translate-x-[-2px] translate-y-[-2px]'
              : 'bg-neo-card text-neo-text border-neo-main hover:bg-white shadow-neo-sm'
          }`}
        >
          <div className={`w-10 h-10 rounded-md border-2 border-neo-main flex items-center justify-center ${
            uploadType === 'brown-bag' ? 'bg-white/20' : 'bg-neo-bg'
          }`}>
            <FaCamera className={uploadType === 'brown-bag' ? 'text-white' : 'text-neo-text'} />
          </div>
          <div className="text-left">
            <p className="font-bold">Brown Bag Photo</p>
            <p className={`text-xs ${uploadType === 'brown-bag' ? 'text-white/90' : 'text-neo-text-sub'}`}>
              Photo of medications
            </p>
          </div>
        </button>
      </div>

      <div className="mb-6">
        {isLoading ? (
          <div className="py-8 text-center border-2 border-dashed border-neo-main rounded-lg bg-neo-bg">
            <div className="relative inline-block mb-4">
              <div className="w-16 h-16 rounded-full bg-white border-2 border-neo-main flex items-center justify-center shadow-neo-sm">
                <FaMagic className="text-2xl text-neo-accent animate-pulse" />
              </div>
              <div className="absolute inset-0">
                <svg className="w-16 h-16 animate-spin" style={{ animationDuration: '2s' }}>
                  <circle cx="32" cy="32" r="30" stroke="#000000" strokeWidth="3" 
                          fill="none" strokeDasharray="50 150" strokeLinecap="round" />
                </svg>
              </div>
            </div>
            <p className="text-neo-text font-bold">AI is analyzing your file...</p>
            <p className="text-neo-text-sub text-sm mt-1">Extracting medication information</p>
          </div>
        ) : (
          <FileUpload 
            onChange={handleFileUpload} 
            accept={uploadType === 'prescription' ? '.pdf,.jpg,.jpeg,.png' : 'image/*'}
          />
        )}
      </div>

      {extractedMeds.length > 0 && (
        <div className="neo-card p-5 bg-white animate-fade-in">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-bold text-neo-text flex items-center gap-2">
              <FaCheckCircle className="text-neo-success" />
              Extracted {extractedMeds.length} Medication(s)
            </h4>
            <span className="text-xs bg-neo-success text-white px-3 py-1 rounded-md border-2 border-neo-main shadow-neo-sm font-bold">
              Review & Edit
            </span>
          </div>

          <div className="space-y-4 mb-5 max-h-96 overflow-y-auto">
            {extractedMeds.map((med) => (
              <MedicationCard
                key={med.id}
                med={med}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))}
          </div>

          <button
            type="button"
            onClick={handleConfirm}
            className="w-full py-4 bg-neo-success text-white border-2 border-neo-main rounded-lg 
                     hover:bg-green-500 font-bold flex items-center justify-center gap-2
                     shadow-neo-brutal active:shadow-none active:translate-x-[3px] active:translate-y-[3px] transition-all"
          >
            <FaCheckCircle />
            Confirm & Add All Medications
          </button>
        </div>
      )}
    </div>
  );
};

const MedicationCard = ({ med, onEdit, onDelete }) => {
  const confidenceColors = {
    high: 'bg-green-100 text-green-700 border-green-200',
    medium: 'bg-amber-100 text-amber-700 border-amber-200',
    low: 'bg-red-100 text-red-700 border-red-200'
  };

  const confidenceClass = confidenceColors[med.confidence] || 'bg-gray-100 text-gray-700 border-gray-200';

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      backgroundColor: '#F5F5DC', // neo-card
      borderColor: '#000000', // neo-main
      borderWidth: '2px',
      boxShadow: state.isFocused ? 'none' : '4px 4px 0px 0px #000000', // neo-brutal
      borderRadius: '6px',
      minHeight: '40px',
      '&:hover': {
        borderColor: '#000000',
      },
    }),
    menu: (base) => ({
      ...base,
      borderRadius: '6px',
      border: '2px solid #000000',
      boxShadow: '4px 4px 0px 0px #000000',
      backgroundColor: '#F5F5DC',
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused ? '#ADD8E6' : '#F5F5DC',
      color: '#323232',
      '&:active': {
        backgroundColor: '#ADD8E6',
      },
    }),
  };

  return (
    <div className="neo-card p-5 mb-4 bg-neo-card">
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1 mr-4">
          <label className="block text-sm font-bold text-neo-text mb-2">
            Generic Name <span className="text-neo-danger">*</span>
          </label>
          <input
            type="text"
            value={med.generic_name}
            onChange={(e) => onEdit(med.id, 'generic_name', e.target.value)}
            className="neo-input"
            placeholder="Enter generic name"
          />
          {med.position && (
            <span className="text-xs text-neo-text-sub mt-1 block">
              Position: {med.position}
            </span>
          )}
        </div>

        <div className="flex gap-2 items-start">
          <span className={`text-xs px-3 py-1.5 rounded-md font-bold border-2 border-neo-main shadow-neo-sm ${confidenceClass}`}>
            {med.confidence || 'unknown'}
          </span>
          <button 
            type="button"
            onClick={() => onDelete(med.id)} 
            className="w-9 h-9 bg-neo-danger text-white border-2 border-neo-main rounded-md shadow-neo-sm flex items-center justify-center hover:bg-red-500 active:shadow-none active:translate-x-[2px] active:translate-y-[2px] transition-all"
            title="Delete medication"
          >
            <FaTrash className="text-sm" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-bold text-neo-text mb-2">Brand Name</label>
          <input
            type="text"
            value={med.brand_name || ''}
            onChange={(e) => onEdit(med.id, 'brand_name', e.target.value)}
            placeholder="Optional"
            className="neo-input"
          />
        </div>

        <div>
          <label className="block text-sm font-bold text-neo-text mb-2">
            Dose <span className="text-neo-danger">*</span>
          </label>
          <input
            type="text"
            value={med.dose}
            onChange={(e) => onEdit(med.id, 'dose', e.target.value)}
            placeholder="e.g., 500 mg"
            className="neo-input"
          />
        </div>

        <div>
          <label className="block text-sm font-bold text-neo-text mb-2">
            Frequency <span className="text-neo-danger">*</span>
          </label>
          <CreatableSelect
            options={FREQUENCY_OPTIONS}
            value={FREQUENCY_OPTIONS.find(opt => opt.value === med.frequency) || 
                   (med.frequency ? { value: med.frequency, label: med.frequency } : null)}
            onChange={(option) => onEdit(med.id, 'frequency', option?.value || '')}
            placeholder="Select..."
            isClearable
            styles={selectStyles}
          />
        </div>

        <div>
          <label className="block text-sm font-bold text-neo-text mb-2">Duration</label>
          <Select
            options={DURATION_OPTIONS}
            value={DURATION_OPTIONS.find(opt => opt.value === med.duration)}
            onChange={(option) => onEdit(med.id, 'duration', option?.value || 'unknown')}
            placeholder="Select..."
            styles={selectStyles}
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-bold text-neo-text mb-2">Indication</label>
          <CreatableSelect
            options={INDICATION_OPTIONS}
            value={INDICATION_OPTIONS.find(opt => opt.value === med.indication) || 
                   (med.indication ? { value: med.indication, label: med.indication } : null)}
            onChange={(option) => onEdit(med.id, 'indication', option?.value || '')}
            placeholder="What is it for?"
            isClearable
            styles={selectStyles}
          />
        </div>
      </div>

      {med.notes && (
        <div className="mt-4 p-4 bg-neo-bg border-2 border-neo-main rounded-md shadow-neo-sm">
          <p className="text-xs font-bold text-neo-text mb-1">AI Extraction Notes:</p>
          <p className="text-sm text-neo-text italic">{med.notes}</p>
        </div>
      )}
    </div>
  );
};

export default MedicationExtractor;
